/**
 * DCS Editor Plugin for Vite
 *
 * Injects the editor bridge script into customer sites when running
 * in portal preview mode (inside the visual editor iframe).
 *
 * The bridge enables:
 * - Inline text editing via contenteditable
 * - Section hover highlights and AI ✨ buttons
 * - postMessage communication with the portal
 *
 * This plugin should only be active in development/preview mode.
 * It's safe to include in production builds — it does nothing unless
 * the page detects it's inside an iframe.
 *
 * @example
 * ```typescript
 * // vite.config.ts
 * import { dcsEditorPlugin } from '@duffcloudservices/cms/plugins'
 *
 * export default defineConfig({
 *   plugins: [
 *     dcsEditorPlugin()
 *   ]
 * })
 * ```
 */

import type { Plugin } from 'vite'

export interface DcsEditorPluginOptions {
  /** Enable debug logging */
  debug?: boolean
}

/**
 * Vite plugin that injects the editor bridge script for portal preview integration.
 *
 * The bridge auto-initializes only when the page detects it's running inside an iframe,
 * so it's safe to include in all builds — it's a no-op in standalone browsing.
 *
 * Uses a virtual module (`/__dcs-editor-bridge.js`) served through Vite's dev server
 * so that bare module specifiers (like `@duffcloudservices/cms/editor`) are properly
 * resolved through Vite's module graph rather than hitting the browser's native ESM
 * resolver, which can't handle bare specifiers.
 */
export function dcsEditorPlugin(options: DcsEditorPluginOptions = {}): Plugin {
  const { debug = false } = options
  const VIRTUAL_PATH = '/__dcs-editor-bridge.js'
  const RIBBON_VIRTUAL_PATH = '/__dcs-preview-ribbon.js'

  // Captured during configResolved — used to gate HTML injection.
  let isDev = false
  let basePath = '/'
  let isPreview = false

  return {
    name: 'dcs-editor-bridge',

    // Preview only: stop Vite running an unreachable HMR WebSocket server.
    // Freshness comes from the cms content/seo plugins (fresh transformIndexHtml
    // re-read) + the portal's ?_hc hard reload, not HMR. NOTE: this alone does
    // NOT silence the browser — Vite still injects @vite/client and calls
    // connect(); the client-side stub in transformIndexHtml is what removes the
    // console errors. A normal local `vite dev` (no DCS_PREVIEW) keeps full HMR.
    config(_config, env) {
      if (env.command === 'serve' && process.env.DCS_PREVIEW === 'true') {
        return { server: { hmr: false } }
      }
    },

    configResolved(config) {
      isDev = config.command === 'serve'
      basePath = config.base ?? '/'
      isPreview = process.env.DCS_PREVIEW === 'true'
    },

    resolveId(id) {
      if (id === VIRTUAL_PATH || id === RIBBON_VIRTUAL_PATH) {
        return id
      }
    },

    load(id) {
      if (id === VIRTUAL_PATH) {
        return `
import { initEditorBridge } from '@duffcloudservices/cms/editor'
// The bridge only activates inside an iframe (portal preview)
if (window.parent !== window) {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setTimeout(initEditorBridge, 200))
  } else {
    setTimeout(initEditorBridge, 200)
  }
}
`
      }

      if (id === RIBBON_VIRTUAL_PATH) {
        return `
import { createApp } from 'vue'
import PreviewRibbon from '@duffcloudservices/cms/components'

function mountRibbon() {
  // Create a detached mount point
  const el = document.createElement('div')
  el.id = 'dcs-preview-ribbon-root'
  document.body.appendChild(el)

  const app = createApp(PreviewRibbon)
  app.mount(el)
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountRibbon)
} else {
  mountRibbon()
}
`
      }
    },

    /**
     * Only inject the editor bridge and ribbon scripts when:
     * 1. Running in dev mode (Vite dev server), AND
     * 2. The app is served at the root path (base === '/')
     *
     * These are virtual modules resolved via absolute paths (e.g.
     * `/__dcs-editor-bridge.js`). When base !== '/', the app is served at a
     * sub-path (e.g. /iron-oak-contractors/) and the absolute virtual paths are NOT
     * routed to the Vite dev server by the reverse proxy — they hit the main
     * DCS server instead and get redirected to portal.duffcloudservices.com
     * (no CORS headers), causing browser console errors.
     *
     * In production builds, App.vue imports and mounts PreviewRibbon
     * directly, so virtual script injection is always redundant anyway.
     */
    transformIndexHtml: {
      order: 'pre',
      handler(html) {
        if (!isDev || basePath !== '/') return html

        if (debug) {
          console.log('[dcs-editor] Injecting editor bridge + preview ribbon script tags')
        }

        // Inject both the editor bridge and preview ribbon scripts
        const editorTag = `<script type="module" src="${VIRTUAL_PATH}"></script>`
        const ribbonTag = `<script type="module" src="${RIBBON_VIRTUAL_PATH}"></script>`
        const bodyHtml = html.replace('</body>', `${editorTag}\n${ribbonTag}\n</body>`)

        if (!isPreview) return bodyHtml

        // Preview only: neutralise Vite's HMR WebSocket. Vite always injects
        // @vite/client and unconditionally calls transport.connect(); behind the
        // preview proxy chain there is no reachable HMR WS, so it logs
        //   WebSocket connection to 'wss://<portal>/?token=…' failed
        //   WebSocket connection to 'wss://localhost:<port>/?token=…' failed
        //   [vite] failed to connect to websocket
        // server.hmr:false does NOT stop this (the client is still injected). A
        // classic <script> in <head> runs during parse, BEFORE the deferred
        // @vite/client module, so returning an inert socket for the 'vite-hmr'
        // subprotocol silences the attempt with zero effect on app/runtime
        // WebSockets. No-op outside preview / in production builds.
        const wsStub =
          `(function(){try{var N=window.WebSocket;if(!N)return;` +
          `function hmr(p){return p==='vite-hmr'||(Array.isArray(p)&&p.indexOf('vite-hmr')!==-1)}` +
          `function Inert(){this.readyState=0}` +
          `Inert.prototype.send=function(){};Inert.prototype.close=function(){};` +
          `Inert.prototype.addEventListener=function(){};Inert.prototype.removeEventListener=function(){};` +
          `var W=function(u,p){if(hmr(p))return new Inert();return new N(u,p)};` +
          `W.prototype=N.prototype;W.CONNECTING=N.CONNECTING;W.OPEN=N.OPEN;W.CLOSING=N.CLOSING;W.CLOSED=N.CLOSED;` +
          `window.WebSocket=W}catch(e){}})()`

        return {
          html: bodyHtml,
          tags: [{ tag: 'script', children: wsStub, injectTo: 'head-prepend' }],
        }
      },
    },
  }
}
